<template>
  <Header />
  <SideBar />
  <main class="ml-64 min-h-screen bg-gray-50">
    <!-- Header Section -->
    <div>
      <div class="px-8 py-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold text-green-800">KYC Validation</h1>
            <p class="mt-2 text-sm text-gray-600">Perform comprehensive KYC validations and view validation history</p>
          </div>
          <button @click="showModal = true" class="bg-green-800 hover:bg-green-700 text-white px-6 py-3
          rounded-md flex items-center
          space-x-2 font-medium text-white bg-gradient-to-r from-green-700
          to-green-800 hover:from-green-700 hover:to-green-700 focus:outline-none focus:ring-2
          focus:ring-offset-2 rounded-lg focus:ring-blue-500 transition-all duration-200 transform hover:scale-[1.02]" v-if="authStore.hasPermissions('run_validations', 'public')">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
            </svg>
            Validate KYC
          </button>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="px-8 py-6">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div class="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div class="flex items-center">
            <div class="flex-shrink-0 bg-green-100 rounded-lg p-3">
              <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-600">Successful Validations</p>
              <p class="text-2xl font-semibold text-gray-900">{{ getSuccessCount() }}</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div class="flex items-center">
            <div class="flex-shrink-0 bg-red-100 rounded-lg p-3">
              <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-600">Failed Validations</p>
              <p class="text-2xl font-semibold text-gray-900">{{ getFailedCount() }}</p>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div class="flex items-center">
            <div class="flex-shrink-0 bg-green-100 rounded-lg p-3">
              <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1">
                </path>
              </svg>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-600">Total Charges</p>
              <p class="text-2xl font-semibold text-gray-900">UGX {{ getTotalCharges() }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Validation Table -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200">
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-medium text-gray-900">Validation History</h3>
            <div class="flex items-center space-x-3">
              <label class="sr-only">Filter by status</label>
              <select aria-label="Filter by status" class="border border-gray-300 rounded-md px-3 py-2 text-sm">
                <option>All Status</option>
                <option>Successful</option>
                <option>Failed</option>
              </select>
              <label class="sr-only">Search validations</label>
              <input type="text" aria-label="Search validations" placeholder="Search..."
                class="border border-gray-300 rounded-md px-3 py-2 text-sm">
            </div>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entity Type
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Identifier</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Identifier Type
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor ID</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Response</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Charge</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="request in requestStore.kycValidationResults" :key="request.id"
                class="hover:bg-gray-50 transition-colors duration-150">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ formatEntityType(request.entityType) }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ maskIdentifier(request.identifier, request.identifierType) }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-500" v-if="request.identifierType ===
                    'ii_drivers_license_permit_number'">Driver's License Permit</div>
                  <div class="text-sm text-gray-500" v-if="request.identifierType ===
                    'ii_refugee_number'">Refugee Number</div>
                  <div class="text-sm text-gray-500" v-if="request.identifierType ===
                    'ii_passport_number'">Passport Number</div>
                  <div class="text-sm text-gray-500" v-if="request.identifierType ===
                    'ii_tax_identification_number'">Tax Identification Number</div>
                  <div class="text-sm text-gray-500" v-if="request.identifierType ===
                    'ii_nssf_number'">NSSF Number</div>
                  <div class="text-sm text-gray-500" v-if="request.identifierType ===
                    'ii_registration_certificate_number'">Business Certification Number</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="getStatusBadgeClass(request.status)"
                    class="inline-flex px-2 py-1 text-xs font-semibold rounded-full">
                    {{ request.status }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-500">{{ request.vendorId?.String || 'N/A' }}</div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm text-gray-900 max-w-xs">
                    <button @click="showResponseDetails = request.response"
                      class="text-green-600 hover:text-green-800 font-medium" title="Click to view full response">
                      View Response
                    </button>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ requestStore.getFormattedCharge(request.charge) }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-500">{{ formatDate(request.createdAt) }}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="px-6 py-4 border-t border-gray-200">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-700">
              Showing <span class="font-medium">{{ requestStore.pagination.currentPage }}</span> of
              <span class="font-medium">{{ requestStore.pagination.totalPages }}</span> pages
            </div>
            <div class="flex items-center space-x-2">
              <button
                class="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                :disabled="requestStore.pagination.currentPage <= 1">
                Previous
              </button>
              <button class="px-3 py-2 text-sm font-medium text-white bg-green-600 border
                border-transparent rounded-md hover:bg-green-700" @click="loadPage(requestStore.pagination.nextPage)">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- KYC Validation Modal -->
    <Modal :show="showModal" title="KYC Validation" size="max-w-4xl" @close="showModal = false">
      <form @submit.prevent="submitValidation" class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label for="entityType" class="block text-sm font-medium text-gray-700 mb-2">Entity Type</label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z">
                  </path>
                </svg>
              </div>
              <select v-model="formData.entityType" id="entityType" required
                class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-indigo-500">
                <option value="">Select entity type</option>
                <option value="0">Individual</option>
                <option value="1">NonIndividual</option>
              </select>
            </div>
            <p class="mt-2 text-sm text-gray-500">Select the type of entity to validate</p>
          </div>

          <div>
            <label for="identifierType" class="block text-sm font-medium text-gray-700 mb-2">Identifier Type</label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2">
                  </path>
                </svg>
              </div>
              <select v-model="formData.identifierType" id="identifierType" required
                class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500">
                <option value="">Select identifier type</option>
                <option value="ii_refugee_number">Refugee ID</option>
                <option value="ii_passport_number">Passport</option>
                <option value="ii_drivers_license_permit_number">Driver's License</option>
                <option value="ii_registration_certificate_number">Business Registration</option>
                <option value="ii_tax_identification_number">Tax ID</option>
                <option value="ii_nssf_number">NSSF Number</option>
              </select>
            </div>
            <p class="mt-2 text-sm text-gray-500">Select the type of identifier to validate</p>
          </div>

          <div class="md:col-span-2">
            <label for="identifier" class="block text-sm font-medium text-gray-700 mb-2">Identifier</label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center justify-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                  </path>
                </svg>
              </div>
              <input v-model="formData.identifier" id="identifier" type="text" class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg
                focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Enter identifier" required />
            </div>
            <p class="mt-2 text-sm text-gray-500">Enter the identifier value for validation</p>
          </div>
        </div>

        <div class="bg-gray-50 rounded-lg p-4" v-if="showResponse">
          <h4 class="text-sm font-medium text-gray-900 mb-2">Validation Details</h4>
          <div class="space-y-2 text-sm text-gray-600">
            <div class="flex justify-between">
              <span>Service:</span>
              <span class="font-medium">KYC Validation</span>
            </div>
            <div class="flex justify-between">
              <span>Cost:</span>
              <span class="font-medium">{{ requestStore.kycValidationResponse.cost }}</span>
            </div>
            <div class="flex justify-between">
              <span>Processing Time:</span>
              <span class="font-medium">500 milliseconds</span>
            </div>
          </div>
        </div>

        <div class="bg-green-50 border border-green-200 rounded-lg p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-green-800">KYC Validation</h3>
              <div class="mt-2 text-sm text-green-700">
                <p>This service performs comprehensive KYC validation against multiple databases and provides detailed
                  verification results.</p>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end space-x-3">
          <button type="button" @click="showModal = false"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" :disabled="loading"
            class="bg-green-800 hover:bg-green-700 text-white px-6 py-3
          rounded-md flex items-center
          space-x-2 font-medium text-white bg-gradient-to-r from-green-700
          to-green-800 hover:from-green-700 hover:to-green-700 focus:outline-none focus:ring-2
          focus:ring-offset-2 rounded-lg focus:ring-blue-500 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed">
            <svg v-if="loading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>
            {{ loading ? 'Validating...' : 'Validate KYC' }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- Response Details Modal -->
    <Modal :show="!!showResponseDetails" title="Response Details" size="max-w-4xl" @close="showResponseDetails = ''">
      <div class="space-y-4">
        <div class="bg-gray-50 rounded-lg p-4">
          <h4 class="text-sm font-medium text-gray-900 mb-2">API Response</h4>
          <div class="bg-white border border-gray-200 rounded-md p-4 overflow-auto max-h-96">
            <pre
              class="text-sm text-gray-800 whitespace-pre-wrap font-mono">{{ formatResponse(showResponseDetails) }}</pre>
          </div>
        </div>
        <div class="flex justify-end">
          <button @click="showResponseDetails = ''"
            class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
            Close
          </button>
        </div>
      </div>
    </Modal>
  </main>
</template>
<script setup lang="ts">
import { onMounted, ref, reactive } from 'vue';
import { useRequestStore } from '@/store/requests';
import { useNotificationStore } from '@/store/notifications';
import { formatCharge } from '@/utils/chargeHandler';
import { useAuthStore  } from '@/store/auth';
import Header from '../components/header.vue';
import SideBar from '../components/sidebar.vue';
import Modal from '../components/modal.vue';

const requestStore = useRequestStore();
const notificationStore = useNotificationStore();
const showModal = ref(false);
const showResponseDetails = ref('');
const loading = ref(false);
const showResponse = ref(false);
const authStore = useAuthStore();

const formData = reactive({
  entityType: 0,
  identifierType: '',
  identifier: '',
});

onMounted(() => {
  const purpose = localStorage.getItem('purpose');
  if (purpose === 'local') {
    requestStore.fetchAdminKYCValidations(1);
  } else {
    requestStore.fetchKYCValidations(1);
  }
});

async function submitValidation() {
  loading.value = true;
  if ((formData.entityType !== 0 && formData.entityType !== 1) || !formData.identifierType || !formData.identifier) {
    notificationStore.showError('Validation Error', 'Please fill in all required fields');
    loading.value = false;
    return;
  }

  try {
    await requestStore.validateKYCRequest(
      formData.identifier,
      formData.entityType,
      formData.identifierType,
    );
    loading.value = false;
    formData.entityType = 0;
    formData.identifier = '';
    formData.identifierType = '';
    loadPage(1);
    notificationStore.showSuccess('KYC Validation', 'KYC validation completed successfully');
  } catch (error) {
    loading.value = false;
    console.error('KYC validation failed:', error);
    notificationStore.showError('Validation Failed', 'Failed to complete KYC validation. Please try again.');
  }
}

function loadPage(page: number) {
  const purpose = localStorage.getItem('purpose');
  if (purpose === 'local') {
    requestStore.fetchAdminKYCValidations(page);
  } else {
    requestStore.fetchKYCValidations(page);
  }
}

function formatDate(input: string) {
  const date = new Date(input);
  const formatted = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  return formatted;
}

function formatEntityType(entityType: string) {
  return entityType.charAt(0).toUpperCase() + entityType.slice(1);
}

function formatIdentifierType(identifierType: string) {
  return identifierType.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

function maskIdentifier(identifier: string, identifierType: string) {
  if (!identifier) return '';

  if (identifierType.toLowerCase() === 'nin' && identifier.length > 4) {
    return identifier.substring(0, 2) + '*'.repeat(identifier.length - 4) + identifier.substring(identifier.length - 2);
  }

  if (identifier.length > 6) {
    return identifier.substring(0, 3) + '*'.repeat(identifier.length - 6) + identifier.substring(identifier.length - 3);
  }

  return identifier;
}

function getStatusBadgeClass(status: string) {
  switch (status.toLowerCase()) {
    case 'success':
    case 'completed':
    case 'verified':
      return 'bg-green-100 text-green-800 border border-green-200';
    case 'failed':
    case 'error':
    case 'rejected':
      return 'bg-red-100 text-red-800 border border-red-200';
    case 'pending':
    case 'processing':
    case 'under_review':
      return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
    default:
      return 'bg-gray-100 text-gray-800 border border-gray-200';
  }
}

function formatResponse(response: string) {
  if (!response) return 'No response data';

  try {
    // Try to parse as JSON to format nicely
    const parsed = JSON.parse(response);
    if (typeof parsed === 'object') {
      return JSON.stringify(parsed, null, 2);
    }
    return response;
  } catch {
    // If not JSON, return as-is
    return response;
  }
}

function getSuccessCount() {
  return requestStore.kycValidationResults?.filter((req) => req.status.toLowerCase() === 'success' || req.status.toLowerCase() === 'completed' || req.status.toLowerCase() === 'verified').length;
}

function getFailedCount() {
  return requestStore.kycValidationResults?.filter((req) => req.status.toLowerCase() === 'failed' || req.status.toLowerCase() === 'error' || req.status.toLowerCase() === 'rejected').length;
}

function getTotalCharges() {
  return requestStore.kycValidationResults?.reduce((total, req) => total + requestStore.getParsedCharge(req.charge), 0);
}
</script>
